import React from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import MuiTimePicker from '@/components/ui/MuiTimePicker';

interface ScheduleTimeRepeatFormProps {
  name: string;
  onNameChange: (v: string) => void;
  start: Date | null;
  onStartChange: (d: Date | null) => void;
  end: Date | null;
  onEndChange: (d: Date | null) => void;
  days: boolean[];
  onToggleDay: (index: number) => void;
}

export const ScheduleTimeRepeatForm: React.FC<ScheduleTimeRepeatFormProps> = ({
  name,
  onNameChange,
  start,
  onStartChange,
  end,
  onEndChange,
  days,
  onToggleDay,
}) => {
  const { mode } = useSelector((state: RootState) => state.theme);

  const durationText = (() => {
    const toMinutes = (d: Date | null) => (d ? d.getHours() * 60 + d.getMinutes() : 0);
    const s = toMinutes(start);
    const e = toMinutes(end);
    const diff = e >= s ? e - s : 1440 - s + e;
    const h = Math.floor(diff / 60);
    const m = diff % 60;
    return `Duration ${h} Hours ${m} Minutes`;
  })();

  return (
    <>
      {/* 时间部分 */}
      <div
        className={classNames(
          'rounded-md border p-4',
          {
            'bg-white border-gray-200': mode === 'light',
            'bg-gray-800 border-gray-700': mode === 'dark',
          }
        )}
      >
        <div className={classNames('text-sm font-medium mb-2', {
          'text-gray-800': mode === 'light',
          'text-gray-200': mode === 'dark',
        })}>Time</div>
        <div className="flex flex-col gap-3">
          <div>
            <label className={classNames('text-xs', {
              'text-gray-500': mode === 'light',
              'text-gray-400': mode === 'dark',
            })}>Schedule Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => onNameChange(e.target.value)}
              className={classNames('mt-1 w-full rounded-md border px-3 py-2 text-sm', {
                'border-gray-300 bg-white text-gray-800': mode === 'light',
                'border-gray-700 bg-gray-900 text-gray-100': mode === 'dark',
              })}
              placeholder="Enter name"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={classNames('text-xs', {
                'text-gray-500': mode === 'light',
                'text-gray-300': mode === 'dark',
              })}>Start Time</label>
              <div className="mt-1">
                <MuiTimePicker
                  value={start || null}
                  onChange={(d) => onStartChange(d)}
                  format="24"
                  className={classNames(
                    // Light mode default MUI styles are fine; add dark overrides
                    'dark:[&_.MuiInputBase-root]:bg-gray-900 dark:[&_.MuiInputBase-input]:text-gray-100 dark:[&_.MuiOutlinedInput-notchedOutline]:border-gray-700 dark:[&_.MuiSvgIcon-root]:text-gray-300 dark:[&_.Mui-focused_.MuiOutlinedInput-notchedOutline]:border-cyan-500'
                  )}
                />
              </div>
            </div>
            <div>
              <label className={classNames('text-xs', {
                'text-gray-500': mode === 'light',
                'text-gray-300': mode === 'dark',
              })}>End Time</label>
              <div className="mt-1">
                <MuiTimePicker
                  value={end || null}
                  onChange={(d) => onEndChange(d)}
                  format="24"
                  className={classNames(
                    'dark:[&_.MuiInputBase-root]:bg-gray-900 dark:[&_.MuiInputBase-input]:text-gray-100 dark:[&_.MuiOutlinedInput-notchedOutline]:border-gray-700 dark:[&_.MuiSvgIcon-root]:text-gray-300 dark:[&_.Mui-focused_.MuiOutlinedInput-notchedOutline]:border-cyan-500'
                  )}
                />
              </div>
            </div>
          </div>
          <div className={classNames('text-xs', {
            'text-gray-500': mode === 'light',
            'text-gray-400': mode === 'dark',
          })}>{durationText}</div>
        </div>
      </div>

      {/* Repeat Time */}
      <div
        className={classNames(
          'rounded-md border p-4',
          {
            'bg-white border-gray-200': mode === 'light',
            'bg-gray-800 border-gray-700': mode === 'dark',
          }
        )}
      >
        <div className={classNames('text-sm font-medium mb-3', {
          'text-gray-800': mode === 'light',
          'text-gray-200': mode === 'dark',
        })}>Repeat Time</div>
        <div className="flex gap-2 flex-wrap">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d, idx) => (
            <button
              key={d}
              onClick={() => onToggleDay(idx)}
              className={classNames(
                'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-200',
                days[idx]
                  ? 'bg-cyan-500 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
              )}
              aria-pressed={days[idx]}
            >
              {d.substring(0, 1)}
            </button>
          ))}
        </div>
      </div>
    </>
  );
};

export default ScheduleTimeRepeatForm;